/* eslint-disable unicorn/prefer-blob-reading-methods */
import angular from 'angular';
import _ from 'lodash-es';
import $ from 'jquery';
import moment from 'moment';
import APIV5 from '~/models/api';
import FileUpload from '~/scripts/services/model/file-upload';
import Form from '~/scripts/services/model/form';
import Utils from '~/scripts/services/utils';
import { ELEMENT_OF } from '../constants/common-kids';

/* globals Knitiv */

/**
 * @class FormService
 */
export default class FormService {
	#$injector;

	#$q;

	/** @type {Knitiv.RootScope} */
	#$rootScope;

	#$translate;

	/** @type {import('~/scripts/services/model/api').default} */
	#API;

	#Evaluator;

	#NodeService;

	#Notification;

	#User;

	// @ts-ignore
	#mfCache;

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	constructor(
		$injector,
	) {
		this.#$injector = $injector;
		this.#$q = this.#$injector.get('$q');
		// @ts-ignore
		this.#$rootScope = this.#$injector.get('$rootScope');
		this.#$translate = this.#$injector.get('$translate');
		this.#API = this.#$injector.get('API');
		this.#Evaluator = this.#$injector.get('Evaluator');
		this.#NodeService = this.#$injector.get('NodeService');
		this.#Notification = this.#$injector.get('Notification');
		this.#User = this.#$injector.get('User');

		/** @type {?Object<import('@knitiv/api-client-javascript').KNode, any>[]} */
		this.#mfCache = null;
	}

	/**
	 * Creates and returns a Form (Node-extended) instance
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the form to create
	 * @param {{new_kid?: import('@knitiv/api-client-javascript').KNode}} options
	 * @returns {Form} Form instance
	 */
	create(isa, options = {}) {
		const form = this.#NodeService.createForm();
		form.kid = options.new_kid ?? `K_NODE;${this.#User.getRefNr()}`;
		form.isa.objid = isa;
		return form;
	}

	/**
	 * Signs the submission
	 *
	 * @param {any} submission Submission object
	 * @returns {string} Identifier string
	 * @deprecated
	 */
	// eslint-disable-next-line class-methods-use-this
	dataSignature(submission) {
		const returnValue = [];
		if (submission) {
			for (const element of Object.values(submission.data)) {
				if (Array.isArray(element)) {
					for (const value of element) {
						if (value.action !== 'DEL') {
							returnValue.push(value.kid);
						}
					}
				}
				else if (element !== undefined) {
					returnValue.push(element);
				}
			}
		}
		return returnValue.join('');
	}

	/**
	 * Delete node
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid KID of the relevant form
	 * @returns {Promise<any>} Promise result
	 */
	delete(kid) {
		return this.#NodeService.delete(kid);
	}

	/**
	 * Saves the node from the form
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid KID of the relevant form
	 * @returns {Promise<any>} Promise result
	 */
	detachFile(kid) {
		return this.#NodeService.detachFile(kid);
	}

	/**
	 * Evaluate a custom script
	 *
	 * @param {string | Function} function_ Script or function to execute
	 * @param {any} arguments_ Arguments of the function
	 * @param {?string} [returnValue] Name of the variable to return
	 * @returns {*} Return result
	 */
	evaluate(function_, arguments_, returnValue = undefined) {
		let function2 = function_;
		let arguments2 = _.merge(arguments_, {
			_, $, moment, isV5: false,
		});
		let returnValue2 = returnValue;
		const component = arguments_.component ?? { key: 'unknown' };
		if (!arguments_.form && arguments_.instance) {
			arguments2.form = _.get(arguments_.instance, 'root._form', {});
		}

		const componentKey = component.key;

		if (typeof function2 === 'string') {
			if (returnValue2) {
				function2 += `;return ${returnValue2}`;
			}

			function2 = this.#Evaluator.evaluator(function2, arguments2);
			arguments2 = _.values(arguments2);
		}

		if (typeof function2 === 'function') {
			returnValue2 = this.#Evaluator.evaluate(function2, arguments2);
		}
		else if (function2) {
			console.warn(`Unknown function type for ${componentKey}`);
		}
		return returnValue2;
	}

	/**
	 * Gets the files in submissions
	 *
	 * @param {any} submissions
	 * @returns {angular.IPromise<any>}
	 */
	getFilesInForm(submissions) {
		const deferred = this.#$q.defer();

		/** @type {any[]} */
		const files = [];

		for (const submission of Object.values(submissions)) {
			/** @type {[import('@knitiv/api-client-javascript').KNode, any][]} */
			// @ts-ignore
			const entries = Object.entries(submission.data);
			for (const [isa, values] of entries) {
				if (Array.isArray(values)) {
					for (const value of values) {
						if (typeof value === 'object' && 'storage' in value && value.storage === 'base64') {
							const array = value.url.split(',');
							const mime = array[0].match(/:(.*?);/)[1];
							const bstr = atob(array[1]);
							let n = bstr.length;
							const data = new Uint8Array(n);
							while (n--) {
								// eslint-disable-next-line unicorn/prefer-code-point
								data[n] = bstr.charCodeAt(n);
							}
							const file = new FileUpload(this.#$injector, new window.File([data], value.name, { type: mime }));
							files.push(file);
							file.data = data;
							file.isa = isa;
						}
					}
				}
			}
		}

		if (files.length > 0) {
			// @ts-ignore
			// eslint-disable-next-line unicorn/no-reduce,promise/catch-or-return,unicorn/no-array-reduce
			files.reduce((p, file) => p.then(file.upload).then(() => {
				for (const submission of Object.values(submissions)) {
					if (file.isa.kid in submission.data) {
						for (const fd of submission.data[file.isa.kid]) {
							if (file.file.name === fd.name) {
								fd.kid = file.kid;
								fd.name = file.name;
							}
						}
					}
				}
				return file;
			}), this.#$q.when(true)).then(() => deferred.resolve(true));
		}
		else {
			deferred.resolve(true);
		}
		return deferred.promise;
	}

	/**
	 * Gets the items for saving
	 *
	 * @param {any} forms Forms collection
	 * @param {Record<
	 *   import('@knitiv/api-client-javascript').KNode,
	 *   import('@knitiv/api-client-javascript').SubmissionEntry>
	 * } submissions Submissions collection
	 * @returns {import('@knitiv/api-client-javascript').Item[] | undefined} Items for saving
	 */
	// eslint-disable-next-line class-methods-use-this
	getItemsForSave(forms, submissions) {
		/**
		 * @type {{
		 *   target_objnum: import('@knitiv/api-client-javascript').KNode | '',
		 *   submission: Record<import('@knitiv/api-client-javascript').KNode, any>
		 * }}
		 */
		const item = {
			target_objnum: '',
			submission:    {},
		};

		// eslint-disable-next-line max-len
		for (const [formKid, submission] of /** @type {[import('@knitiv/api-client-javascript').KNode,any][]} */ (Object.entries(submissions))) {
			/** @type {?Form} */
			const form = formKid in forms ? forms[formKid] : null;

			// the first form is the target_objnum
			if (!item.target_objnum) {
				// @ts-ignore
				item.target_objnum = formKid;
			}

			if (form) {
				item.submission[formKid] = {
					isa:    form.isa.objid,
					params: submission.params ?? {},
				};

				if (form.new_isa) {
					item.submission[formKid].new_isa = form.new_isa;
				}

				// Files
				if (this.#$rootScope.kds) {
					item.submission[formKid].files = submission.files;
				}
				else {
					if (form.params.editableTabFile && submission.files?.length === 1 && 'fileid' in submission.files[0]) {
						item.submission[formKid].fileid = submission.files[0].fileid;
					}
				}

				if (
					(form.params.editableTabForm || form.params.editableTabForm === null)
					&& form.map
					&& form.modified
				) {
					item.submission[formKid].data = {};
					item.submission[formKid].map = {};

					for (const key of Object.keys(submission.data)) {
						const data = angular.copy(submission.data[key]);
						if (key === 'KREPRE') {
							const component = form.getComponent(key);
							if (component) {
								const { rules } = component.validate;
								item.submission[formKid].data.KREPRE = data.filter(
									(/** @type {import('~/scripts/services/model/repre').default} */ repre) => repre?.lang?.kid
										&& (
											(repre?.lang?.kid in rules && (
												rules[repre.lang.kid].submit === true
												|| !('submit' in rules[repre.lang.kid])
											))
											|| !(repre.lang.kid in rules)
										)
								);
							}
							else {
								item.submission[formKid].data.KREPRE = data;
							}
						}
						else if (key in form.map && form.map[key]?.type && form.checkSubmit(key)) {
							switch (form.map[key].type) {
							case 'datetime': {
								if (data instanceof Date && !form.map[key].enableTime) {
									item.submission[formKid].data[key] = `${moment(data)
										.format('YYYY-MM-DD')}Z`;
								}
								else {
									// @ts-ignore
									item.submission[formKid].data[key] = data === undefined ? null : data;
								}
								break;
							}
							case 'jsoneditor': {
								if (data) {
									// @ts-ignore
									item.submission[formKid].data[key] = data;
								}
								else {
									item.submission[formKid].data[key] = form.map[key].preferText ? '' : null;
								}
								break;
							}
							case 'mf': {
								item.submission[formKid].data[key] = Array.isArray(data) && data.length === 0 ? null : data;
								break;
							}
							case 'password': {
								item.submission[formKid].data[key] = data.name?.trim() === '' ? null : data;
								break;
							}
							case 'smartfield': {
								item.submission[formKid].data[key] = data?.name.trim() === '' ? null : data;
								break;
							}
							default: {
								item.submission[formKid].data[key] = data === undefined ? null : data;
							}
							}
						}
					}

					for (const [key, kdata] of Object.entries(form.map)) {
						if (kdata?.submit === true) {
							item.submission[formKid].map[key] = {
								path:     kdata.path,
								type:     kdata.type,
								type_kid: kdata.type_kid,
							};
						}
					}
					if (Object.keys(item.submission[formKid].data).length === 0) {
						delete item.submission[formKid];
					}
				}

				if (form.params.editableTabAttribut && form.formAttribut?.$dirty) {
					item.submission[formKid].kattribut = form.attribut.map(
						(/** @type {import('~/scripts/services/model/attribut').default} */ a) => a.export(),
					);
				}

				if (form.params.editableTabRepre && form.formRepre?.$dirty && submission.krepre) {
					// @ts-ignore
					item.submission[formKid].krepre = submission.krepre.map(
						(/** @type {import('~/scripts/services/model/repre').default} */ r) => r.export(),
					);
				}

				if (form.params.editableTabRights && form.formRights?.$dirty && item.submission[formKid] !== undefined) {
					item.submission[formKid].kdroit = form.droit.map(
						(/** @type {import('~/scripts/services/model/droit').default} */ d) => d.export(),
					);
					item.submission[formKid].kdroitcust = form.droitcust.map(
						(/** @type {import('~/scripts/services/model/droit-cust').default} */ d) => d.export(),
					);
					item.submission[formKid].kdroitdef = form.droitdef.map(
						(/** @type {import('~/scripts/services/model/droit-def').default} */ d) => d.export(),
					);
				}

				if (form.params.editableTabWF && form.workflow.length > 0 && submission.wf) {
					for (const wf of submission.wf) {
						if (!wf.node_kid) {
							wf.node_kid = formKid;
						}
					}
					item.submission[formKid].wf = submission.wf;
				}

				if (form.linkTo.length > 0) {
					item.submission[formKid].linkTo = form.linkTo.map((/** @type {any} */ linkTo) => ({
						path: Utils.reversePath(linkTo.path),
					}));
				}
			}
		}

		if (!item.target_objnum) {
			return undefined;
		}

		// @ts-ignore
		return [item];
	}

	/**
	 * Creates a form of an existing node
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the relevant form
	 * @param {any} options Options to send to API
	 * @returns {Promise<any>} Promise result
	 */
	async load(isa, options) {
		let modified = false;
		if (options?.modified !== undefined) {
			// eslint-disable-next-line prefer-destructuring
			modified = options.modified;
			delete options.modified;
		}
		options.node_kid = isa;
		const data = await APIV5.instance.formGet(options, { id: 'form-service.load' });
		if (data?.result && data.result.length > 0) {
			/** @type {any} */
			const result = data.result[0];
			// const result = {
			// 	...data.result[0],
			// 	nrReplace: data._NR_replace,
			// };

			if (!('submission' in result)) {
				result.submission = { data: {} };
			}

			result.form = new Form(this.#$injector, {
				...result.form,
				create: !!options.create,
				modified,
				new_isa: result.new_isa,
			}, result.submission.data);

			return result;
		}
		return false;
	}

	/**
	 * Loads the "business" classes
	 *
	 * @returns {Promise<any>}
	 */
	async loadClasses() {
		const classes = [
			{
				kid:  'K_NODE;SYS_CLASSE',
				name: this.#$translate.instant('form.document-type'),
				icon: 'file',
			},
		];

		const result = await APIV5.instance.objectList({
			classKid: 'K_NODE;SYS_CLASSE',
			condition: {
				link: {
					objnum2:      'K_NODE;BUSINESS_CLASS',
					class_kid:    'K_NODE;SYS_CLASSE',
					linker_kid:   '0',
					relation_kid: ELEMENT_OF,
				},
			},
			options: {},
		}, { id: 'form-service.load-classes' });

		let langId = null;
		const currentLang = this.#User.getContext().lang[0];
		for (const [index, values] of Object.entries(result.list)) {
			if (values !== undefined) {
				if ('K_NODE;UNIVERSEL' in values.repre_all) {
					langId = 'K_NODE;UNIVERSEL';
				}
				else if (Object.prototype.hasOwnProperty.call(values.repre_all, currentLang)) {
					langId = currentLang;
				}
				else {
					langId = Object.keys(values)[0];
				}

				if (langId in values.repre_all) {
					classes.push({
						kid:  index,
						name: values.repre_all[langId]?.name,
						icon: values.icon,
					});
				}
			}
		}
		return classes;
	}

	/**
	 * Creates a generic form of an existing node
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa
	 * @param {any} options
	 * @returns {Promise<any>}
	 */
	async loadGeneric(isa, options) {
		/** @type {any} */
		let returnValue;

		_.merge(options, { kform_kid: 0, node_kid: isa });

		const data = await APIV5.instance.formGet(options, { id: 'form.load-generic' });
		for (const result of data.result) {
			if (result.generic && returnValue === undefined) {
				returnValue = new Form(this.#$injector, result.form, data);
			}
		}

		return returnValue;
	}

	/**
	 * Creates a dedicated form of an existing node for repre edition only
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid KID of the relevant form
	 * @returns {Promise<any>} Promise result
	 */
	async loadRepre(kid) {
		const node = await APIV5.instance.objectInfo({ kid, get: ['repre'] }, { id: 'form-service.load-repre' });

		return {
			form: new Form(
				this.#$injector,
				{
					components: [node.repre.component],
					create: false,
					id: `KF${kid.split(';')[1]}`,
					isa: node.isa,
					kid,
					kright: node.kright,
					params: {
						editableTabForm: true,
					},
				},
				node.repre.submission,
			),
			submission: {
				data: node.repre.submission,
			},
		}
	}

	/**
	 * Lock node
	 *
	 * @param {Form} form
	 * @returns {Promise<any>}
	 */
	lock(form) {
		return this.#NodeService.lock(form);
	}

	/**
	 * Get lock informations
	 *
	 * @param {Form} form Form instance
	 * @returns {Promise<any>} Promise result
	 */
	lockInfo(form) {
		return this.#NodeService.lockInfo(form);
	}

	/**
	 * Adds new item to the MF cache storage
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the relevant form
	 * @param {any} item Item created
	 * @param {import('@knitiv/api-client-javascript').KNode} formKid KID of the relevant form
	 */
	mfCacheAdd(isa, item, formKid) {
		if (!this.#mfCache) {
			this.#mfCache = {
				[isa]: [],
			};
		}
		else if (!(isa in this.#mfCache)) {
			this.#mfCache[isa] = [];
		}
		if (item.name) {
			this.#mfCache[isa].push({
				item,
				words: item.name.normalize('NFD').replaceAll(/[\u0300-\u036F]/g, '').toLocaleLowerCase().split(/\W+/),
				form:  formKid,
			});
		}
	}

	/**
	 * Clears the MF cache storage
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} isa ISA of the relevant form
	 */
	mfCacheDelete(isa) {
		if (isa in this.#mfCache) {
			this.#mfCache[isa].length = 0;
			delete this.#mfCache[isa];
		}
	}

	/**
	 * Retrieves the items from the  MF cache storage
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode |
	 *   import('@knitiv/api-client-javascript').KNodeMF} isa ISA of the relevant form
	 * @returns {any[]} Items from cache
	 */
	mfCacheGet(isa) {
		return this.#mfCache && isa in this.#mfCache ? this.#mfCache[isa] : [];
	}

	/**
	 * Imports an object from a JSON file
	 *
	 * @param {File} file
	 * @returns {import('angular').IPromise<any>}
	 */
	objectImportJSON(file) {
		const reader = new FileReader();
		const deferred = this.#$q.defer();

		// Closure to capture the file information.
		reader.addEventListener('load', async event => {
			const item = typeof event.target?.result === 'string' ? JSON.parse(event.target?.result) : null;
			if (item) {
				try {
					const result = await this.#API.objectImportJSON([item], { id: 'form-service.object-import-json' });
					if (result?.items.length > 0) {
						this.#Notification.info({
							title:   this.#$translate.instant('common.info'),
							message: 'Votre fichier a bien été importé',
						});
						deferred.resolve(result.items[0]);
					}
				}
				catch {
					this.#Notification.error({
						title:   this.#$translate.instant('error.title'),
						message: 'Fichier JSON incorrect !',
					});
					deferred.reject();
				}
			}
		});

		// Read in the image file as a data URL.
		reader.readAsText(file);

		return deferred.promise;
	}

	/**
	 * Reloads the form
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} nodeKid Node KID
	 * @param {any} options Options to send to API
	 * @param {Object<import('@knitiv/api-client-javascript').KNode, Form>} forms Forms instances
	 * @param {Object<import('@knitiv/api-client-javascript').KNode, any>} submissions Submission data
	 * @returns {Promise<any>} Promise result
	 */
	refresh(nodeKid, options, forms, submissions) {
		// force modified flag in order to submit actual form to KFormSet
		Object.values(forms).map((/** @type {Form} */ form) => {
			form.modified = true;
			return form;
		});
		if (forms && submissions) {
			options.items = this.getItemsForSave(forms, submissions);
		}

		return this.load(nodeKid, options);
	}

	/**
	 * Resets the form (just clearing the MF Cache)
	 */
	reset() {
		this.#mfCache = {};
	}

	/**
	 * Saves the node from the form
	 *
	 * @param {Object<import('@knitiv/api-client-javascript').KNode, Form>} forms Forms collection
	 * @param {Object<import('@knitiv/api-client-javascript').KNode, any>} submissions Submissions collection
	 * @returns {Promise<any>} Promise result
	 */
	async save(forms, submissions) {
		const items = this.getItemsForSave(forms, submissions);
		if (items) {
			const result = await APIV5.instance.formSet({ items }, { id: 'form-service.save' });
			this.reset();
			return result?.result;
		}

		return false;
	}

	/**
	 * Unlock node
	 *
	 * @param {Form} form Form instance
	 * @returns {*} Promise result
	 */
	unlock(form) {
		return this.#NodeService.unlock(form);
	}

	/**
	 * Update Repre
	 *
	 * @param {Form} form
	 * @param {any} submission
	 * @returns {Promise<string | undefined>}
	 */
	async updateRepre(form, submission) {
		if (!form?.kid || !submission?.KREPRE) {
			return;
		}
		const nodeRenameSetResult = await APIV5.instance.nodeRenameSet({
			node_kid: form.kid,
			submission: {
				KREPRE: submission.KREPRE.map((/** @type {any} */ s) => ({
					kid:  s.kid,
					lang:  s.lang,
					type:  s.type,
					value: s.value,
				}))
			},
		}, { id: 'form-service.update-repre' });
		return nodeRenameSetResult?.name?.value ?? undefined;
	}
}

const formService = /* @ngInject */ (
	/** @type {import('angular').auto.IInjectorService} */ $injector,
) => new FormService($injector);

angular.module('app')
	.factory('FormService', formService);
